// SPDX-License-Identifier: AGPL-3.0-only
// Copyright (C) 2025 realvirtual GmbH <https://realvirtual.io>

/**
 * SnapChainPreview — visual highlight of every asset that will follow when
 * the focused (hovered or selected) asset is moved in chain mode.
 *
 * Lifecycle:
 *   showForRoots(roots) walks the paired-snap graph from each focus root,
 *                       unions the members, removes the focus roots themselves
 *                       (those already carry the vivid-green hover/selection
 *                       outline), and renders the remainder as a pale-green
 *                       OutlinePass silhouette via the viewer's outline
 *                       manager "chain" channel. Caching by member-set avoids
 *                       churn on hover flicker / per-frame drag ticks.
 *   showFor(root)       convenience for a single focus root.
 *   hide()              clears the chain channel.
 *
 * Using the OutlinePass (rather than a mesh-glow-hull overlay) makes the chain
 * members read as the same kind of crisp silhouette as the focused object —
 * just paler — and tracks live mesh transforms automatically (no per-frame
 * matrix sync). On WebGPU the outline manager is unavailable and this becomes
 * a no-op, exactly like the focused-object outline.
 */

import type { Object3D } from 'three';
import type { RVViewer } from '../../core/rv-viewer';
import type { SnapPointRegistry } from '../../core/engine/rv-snap-point-registry';

export class SnapChainPreview {
  private readonly viewer: RVViewer;
  private readonly registry: SnapPointRegistry;

  /** Chain members currently outlined (focus roots excluded). */
  private members: Object3D[] = [];
  /** Focus roots the current preview is anchored at. */
  private currentRoots: Object3D[] = [];
  /** Cache key (sorted member uuids) — avoids re-pushing an unchanged set. */
  private currentKey = '';

  constructor(viewer: RVViewer, registry: SnapPointRegistry) {
    this.viewer = viewer;
    this.registry = registry;
  }

  /** Reveal the chain preview for a single focus root. */
  showFor(root: Object3D): void {
    this.showForRoots([root]);
  }

  /**
   * Reveal a pale-green outline on every asset transitively connected to any
   * of `focusRoots` via paired snap points. The focus roots themselves are
   * NOT outlined here — they already carry the hover/selection outline.
   */
  showForRoots(focusRoots: Object3D[]): void {
    const focus = focusRoots.filter((r): r is Object3D => !!r);
    const focusSet = new Set<Object3D>(focus);

    const memberSet = new Set<Object3D>();
    for (const root of focus) {
      for (const member of this.registry.walkChain(root)) {
        if (!focusSet.has(member)) memberSet.add(member);
      }
    }

    const list = [...memberSet];
    const key = list.map((o) => o.uuid).sort().join('|');
    this.currentRoots = focus;
    if (key === this.currentKey) return; // no-op when the member set is unchanged

    this.currentKey = key;
    this.members = list;
    this.viewer.outlineManager?.setChainOutlined(list);
    this.viewer.markRenderDirty?.();
  }

  /** Clear the chain preview. */
  hide(): void {
    if (this.currentKey === '' && this.currentRoots.length === 0 && this.members.length === 0) return;
    this.currentKey = '';
    this.currentRoots = [];
    this.members = [];
    this.viewer.outlineManager?.clearChain();
    this.viewer.markRenderDirty?.();
  }

  /** Re-evaluate the current chain — call when an edge was added/broken while
   *  the preview is showing (e.g. a magnetic snap engaged mid-drag). */
  refresh(): void {
    const roots = this.currentRoots;
    if (roots.length === 0) return;
    this.currentKey = ' force'; // sentinel that no real key can equal
    this.showForRoots(roots);
  }

  /** Test helper — number of chain members currently outlined. */
  getHandleCount(): number { return this.members.length; }
  /** Test helper — primary focus root the preview is anchored at, or null. */
  getCurrentRoot(): Object3D | null { return this.currentRoots[0] ?? null; }
}
